import React, { useRef } from "react";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

ChartJS.register(...registerables);

const utahData = "/img/headerlogo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

const EngTable12 = ({ data }) => {
  const pdfRef = useRef();

  // Convert dataset to cleaner numeric format
  const years = ["2018", "2019", "2020", "2021", "2022", "2023", "2024"];

  const dataset = data.map((item) => ({
    ...item,
    values: years.map((year) => Number(item[`year_${year}`])),
  }));

  // Summary Calculations
  const totalValues = dataset.flatMap((item) => item.values);
  const avgEmployment = (
    totalValues.reduce((a, b) => a + b, 0) / totalValues.length
  ).toFixed(2);

  const yearlyTotals = years.map((_, index) =>
    dataset.reduce((sum, row) => sum + row.values[index], 0)
  );

  const maxYearIndex = yearlyTotals.indexOf(Math.max(...yearlyTotals));
  const minYearIndex = yearlyTotals.indexOf(Math.min(...yearlyTotals));

  const trend =
    yearlyTotals[yearlyTotals.length - 1] > yearlyTotals[0]
      ? "Increasing 📈"
      : yearlyTotals[yearlyTotals.length - 1] < yearlyTotals[0]
        ? "Decreasing 📉"
        : "Stable ➖";

  // Chart Setup
  const chartData = {
    labels: years,
    datasets: dataset.map((item, i) => ({
      label: item.education_label,
      data: item.values,
      borderColor: `hsl(${i * 55}, 70%, 50%)`,
      backgroundColor: `hsl(${i * 55}, 70%, 60%, 0.25)`,
      borderWidth: 2,
      tension: 0.4,
      pointRadius: 5,
      pointHoverRadius: 7,
    })),
  };

  const options = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "Employment by Education Level (2018–2024)",
        font: { size: 18, weight: "bold" },
      },
    },
    scales: {
      y: {
        title: { display: true, text: "Employment Count" },
      },
      x: {
        title: { display: true, text: "Year" },
      },
    },
  };

  // PDF Export
  const downloadPDF = () => {
    const element = pdfRef.current;

    html2canvas(element, { scale: 2 }).then((canvas) => {
      const pdf = new jsPDF("p", "mm", "a4");

      const width = pdf.internal.pageSize.getWidth() - 10;
      const height = (canvas.height * width) / canvas.width;

      pdf.addImage(utahData, "PNG", 10, 5, 30, 25);
      pdf.setFontSize(22);
      pdf.text("UTahData.org", 50, 20);
      pdf.line(10, 32, 200, 32);

      pdf.addImage(canvas.toDataURL("image/png"), "PNG", 5, 35, width, height);

      pdf.line(10, 260, 200, 260);
      pdf.addImage(utahData, "PNG", 20, 265, 20, 14);
      pdf.setFontSize(10);

      pdf.text("Built by", 45, 276);
      pdf.addImage(bodex, "PNG", 58, 268, 40, 10);
      pdf.text("using", 105, 276);
      pdf.addImage(kpi, "PNG", 118, 265, 18, 18);

      pdf.text("Version: 20250916.v3.198", 150, 276);
      pdf.text(`Exported: ${new Date().toLocaleDateString()}`, 145, 292);
      pdf.text(`Source: ${window.location.href}`, 10, 292);

      pdf.save("EngTable12.pdf");
    });
  };

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white shadow-lg rounded-xl">
        <h2 className="text-2xl font-bold text-center text-gray-900 mb-4">
          Employment by Education Level (2018–2024)
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <div className="p-4 rounded-lg shadow bg-blue-50">
            <p className="font-semibold text-blue-900">📈 Highest Year</p>
            <p className="text-gray-700">
              {years[maxYearIndex]} —{" "}
              {yearlyTotals[maxYearIndex].toLocaleString("en-US")}
            </p>
          </div>
          <div className="p-4 rounded-lg shadow bg-red-50">
            <p className="font-semibold text-red-800">📉 Lowest Year</p>
            <p className="text-gray-700">
              {years[minYearIndex]} —{" "}
              {yearlyTotals[minYearIndex].toLocaleString("en-US")}
            </p>
          </div>
          <div className="p-4 rounded-lg shadow bg-green-50">
            <p className="font-semibold text-green-800">📊 Avg Employment</p>
            <p className="text-gray-700">
              {Number(avgEmployment).toLocaleString("en-US")}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Table */}
          <div className="overflow-x-auto mb-6">
            <table className="w-full text-left shadow-md border rounded-lg overflow-hidden">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Education Level</th>
                  {years.map((yr) => (
                    <th key={yr} className="p-2">
                      {yr}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dataset.map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-100">
                    <td className="font-semibold p-2">{row.education_label}</td>
                    {row.values.map((val, i) => (
                      <td key={i} className="p-2">
                        {val.toLocaleString("en-US")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[600px] w-full h-96 p-4">
              <Line data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* PDF Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
};

export default EngTable12;
